import React, { FormEvent, useState } from "react";
import { getCaseLink, getProductData } from "../../../services/productService";
import ApiClient from "../../../services/apiClient";
import Paper from "@mui/material/Paper";
import FormFader from "../../../components/Form/Common/Fader/FormFader";
import wizardStyle from "../wizardStyle";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import { useNavigate } from "react-router";
import { requiredValidator } from "../../../services/validationRules";
import { useForm } from "../../../provider/Form/FormProvider";
import Typography from "@mui/material/Typography";
import Group, { GroupProps } from "../../Wizard/Group";
import { useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "../../../services/reactQuery/reactQueryService";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { Box } from "@mui/material";
import LegalbirdLoader from "../../ContentLoader/LegalbirdLoader";
import { contractLawDefaultCaseOptions } from "../../../services/contractLaw/contractLawDefaultCaseOptions";
import { translate } from "../../../services/translations/translations";
import _ from "lodash";
import * as applicationStatuses from "../../../services/contractLaw/applicationStatus";

type ContractLawCaseSelectionProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
  isLoading?: boolean;
  options?: GroupProps["options"];
  applicationStatus: number;
};

export default function ContractLawCaseSelection({
  product,
  submitProps,
  isLoading,
  options = contractLawDefaultCaseOptions,
  applicationStatus,
}: ContractLawCaseSelectionProps) {
  const navigate = useNavigate();
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const { values, handleSubmit } = useForm();
  const queryClient = useQueryClient();

  const handleSubmitInternal = async (e: FormEvent<HTMLFormElement>) => {
    //-- check if switch is required
    if (values.name !== product.name) {
      e.preventDefault();
      setIsLoadingInternal(true);
      const switchResponse = await ApiClient.post(`/product_switch_case/${product.id}/${values.name}`);
      const newCase = await queryClient.fetchQuery({
        queryKey: queryKeys.item(getProductData(switchResponse.name, "apiUri"), switchResponse.id),
        queryFn: () => fetchResource(getProductData(switchResponse.name, "apiUri"), switchResponse.id),
      });

      let nextApplicationStatus = applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE;

      /** This is the case if the customer is not sure about what contract law product to pick, should route into the not sure application step which is 12 */
      if (newCase.name === "otherContract" && newCase.applicationStatus === applicationStatuses.APPLICATION_STATUS_CONTRACT_CATEGORY) {
        nextApplicationStatus = applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION;
      }

      const updatedCase = await updateResource({
        uri: getProductData(switchResponse.name, "apiUri"),
        id: switchResponse.id,
        data: {
          applicationStatus: nextApplicationStatus,
        },
      });

      queryClient.setQueryData(queryKeys.item(getProductData(switchResponse.name, "apiUri"), switchResponse.id), updatedCase);

      navigate(getCaseLink(updatedCase) + "/antrag");
      return;
    }
    handleSubmit(e);
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmitInternal}>
          {isLoading ? (
            <LegalbirdLoader />
          ) : (
            <>
              <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
                Vertragsart
              </Typography>
              <Group
                type="radio"
                name="name"
                question={translate(`${product.name}.caseSelection.question`, { ..._.cloneDeep(product), applicationStatus })}
                options={options}
                validators={[requiredValidator("Bitte geben Sie die Art des Vertrags an.")]}
              />
            </>
          )}
          <FormSubmit {...submitProps} isLoading={isLoadingInternal || submitProps.isLoading || isLoading} />
        </Box>
      </FormFader>
    </Paper>
  );
}
